import { useTranslation } from '../../../shared/i18n';

import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Table, Button, Space, Tag, Divider, Input, Select, Form,
    Typography, message, Popconfirm, Radio, Tooltip, Row, Col,
    Alert, Dropdown, Menu, Modal, Progress, Switch, Collapse, DatePicker, Statistic,
    List, Empty, Drawer
} from 'antd';
import {
    DownOutlined, PlusOutlined, DeleteOutlined, SaveOutlined, CloseOutlined,
    SearchOutlined, FilterOutlined, CloudUploadOutlined,
    EditOutlined, SettingOutlined,
    QuestionCircleOutlined, FileExcelOutlined, FileTextOutlined,
    InfoCircleOutlined, SyncOutlined, MessageOutlined, RocketOutlined,
    WarningOutlined, SafetyCertificateOutlined, LineChartOutlined,
    BarChartOutlined, GlobalOutlined, CloudFilled, CloudOutlined, NodeIndexOutlined
} from '@ant-design/icons';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dnsService from '../../services/dnsService';
import apiClient from '../../../shared/services/apiClient';
import { billingService } from '../../services/billingService';
import GeoSelector from '../../../shared/components/GeoSelector';
import IspSelector from '../../../shared/components/IspSelector';
import CidrSelector from '../../../shared/components/CidrSelector';
import RecordBulkImportModal from '../../../shared/components/RecordBulkImportModal';
import DnsDnssec from './DnsDnssec';
import { recordContentRule, CONTENT_PLACEHOLDERS } from './recordContentRules';


import { createStyles } from 'antd-style';
import { userPath } from '../../../shared/config/paths';
import ResourcePlanCard from '../../../shared/components/ResourcePlanCard';
import CountryFlag from '../../../shared/components/CountryFlag';

import '../../../styles/DnsRecords.css';
const { Title, Text } = Typography;
const { Option } = Select;
const EditableCell = ({
                          editing, dataIndex, title, record, index, children, inputType, options, ...restProps
                      }) => {
    const { t } = useTranslation();
    let inputNode;

    const form = Form.useFormInstance();
    const currentType = form.getFieldValue('type');

    // 检查当前记录类型是否为A、AAAA或CNAME
    // 对于新记录或编辑现有记录，使用表单中的type值
    // 如果表单中没有type值（可能是刚开始编辑），则使用记录中的type
    const recordType = editing ? (currentType || record?.type) : record?.type;
    const isGeoEnabled = recordType === 'A' || recordType === 'AAAA' || recordType === 'CNAME';
    // 监听类型变化，更新相关字段的显示状态
    useEffect(() => {
        if (editing && dataIndex === 'type') {
            // 在type字段的值变化时，表单会自动重新渲染
            form.setFields([{ name: 'type', touched: true }]);
        }
    }, [editing, dataIndex, form]);

    if (dataIndex === 'route') {
        // 调度线路列 - 渲染隐藏的表单字段以注册到表单
        if (editing && isGeoEnabled) {
            inputNode = (
                <>
                    {/* 隐藏的 geoData 字段 */}
                    <Form.Item name="geoData" hidden>
                        <input type="hidden" />
                    </Form.Item>
                    {/* 隐藏的 ispData 字段 */}
                    <Form.Item name="ispData" hidden>
                        <input type="hidden" />
                    </Form.Item>
                    {/* 隐藏的 cidr_id 字段 */}
                    <Form.Item name="cidr_id" hidden>
                        <input type="hidden" />
                    </Form.Item>
                </>
            );
        }
    } else if (dataIndex === 'geo') {
        // 处理地理位置编辑 (保留兼容性)
        if (editing && isGeoEnabled){
            inputNode = (
                <Form.Item
                    name="geoData"
                    style={{ margin: 0 }}
                >
                    <GeoSelector />
                </Form.Item>
            );
        }
    } else if (dataIndex === 'isp') {
        // 处理ISP编辑 (保留兼容性)
        if (editing && isGeoEnabled) {
            inputNode = (
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.geoData?.country_code !== currentValues.geoData?.country_code
                    }
                >
                    {({ getFieldValue }) => {
                        const geoData = getFieldValue('geoData') || {};
                        const geoCountryCode = geoData.country_code || '';

                        return (
                            <Form.Item
                                name="ispData"
                                style={{margin: 0}}
                            >
                                <IspSelector geoCountry={geoCountryCode} />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            );
        }
    } else if (dataIndex === 'cidr_id') {
        // 处理CIDR编辑 (保留兼容性)
        if (editing && isGeoEnabled) {
            inputNode = (
                <Form.Item
                    name="cidr_id"
                    style={{margin: 0}}
                >
                    <CidrSelector />
                </Form.Item>
            );
        }
    } else if (dataIndex === 'cdn_enabled') {
        // 处理CDN开关编辑
        if (editing) {
            const isCdnSupported = ['A', 'AAAA', 'CNAME'].includes(record?.type);
            inputNode = (
                <Form.Item
                    name="cdn_enabled"
                    valuePropName="checked"
                    style={{margin: 0}}
                >
                    <Switch
                        checkedChildren={t('Enable')}
                        unCheckedChildren={t('Disable')}
                        disabled={!isCdnSupported}
                    />
                </Form.Item>
            );
        }
    } else if (inputType === 'select') {
        inputNode = (
            <Form.Item
                name={dataIndex}
                style={{ margin: 0 }}
                rules={[{ required: true, message: t('Please enter {{title}}!', { title }) }]}
            >
                <Select>
                    {options.map(option => (
                        <Option key={option.value} value={option.value}>{option.label}</Option>
                    ))}
                </Select>
            </Form.Item>
        );
    } else if (inputType === 'textarea') {
        inputNode = (
            <Form.Item
                name={dataIndex}
                style={{ margin: 0 }}
                rules={dataIndex === 'comment' ? [] : dataIndex === 'content'
                    ? [recordContentRule(() => form.getFieldValue('type') || record?.type)]
                    : [{ required: true, message: t('Please enter {{title}}!', { title }) }]}
            >
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} placeholder={dataIndex === 'content' ? (CONTENT_PLACEHOLDERS[recordType] ? t(CONTENT_PLACEHOLDERS[recordType]) : undefined) : undefined} />
            </Form.Item>
        );
    } else {
        inputNode = (
            <Form.Item
                name={dataIndex}
                style={{ margin: 0 }}
                rules={dataIndex === 'comment' ? [] : dataIndex === 'content'
                    ? [recordContentRule(() => form.getFieldValue('type') || record?.type)]
                    : [{ required: true, message: t('Please enter {{title}}!', { title }) }]}
            >
                <Input placeholder={dataIndex === 'content' ? (CONTENT_PLACEHOLDERS[recordType] ? t(CONTENT_PLACEHOLDERS[recordType]) : undefined) : undefined} />
            </Form.Item>
        );
    }

    // 对于 route 列，编辑时同时显示隐藏字段和原始内容（可点击打开Modal）
    if (dataIndex === 'route' && editing) {
        return (
            <td {...restProps}>
                {inputNode}
                {children}
            </td>
        );
    }

    return (
        <td {...restProps}>
            {editing ? inputNode : children}
        </td>
    );
};

const DnsRecords = () => {
    const { t } = useTranslation();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const isMobile = windowWidth < 768;

    // 监听窗口尺寸变化
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const useStyle = createStyles(({ css, token }) => {
        const { antCls } = token;
        return {
            customTable: css`
      ${antCls}-table {
        ${antCls}-table-container {
          ${antCls}-table-body,
          ${antCls}-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `,

        };
    });
    const {styles} = useStyle();
    console.log(styles)
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: (total) => t('{{total}} records total', { total })
    });
    const [filters, setFilters] = useState({
        search: '',
        type: '',
        geo: ''
    });
    const [editingKey, setEditingKey] = useState('');
    const [newRecord, setNewRecord] = useState(null);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { zoneId } = useParams();
    const location = useLocation();
    const [zoneName, setZoneName] = useState(location.state?.zoneName || '');
    const [zoneNsMigrating, setZoneNsMigrating] = useState(false);
    // 该区域实际分配的 NS（来自 getZone.nameservers，与 check-ns 校验同源）
    const [zoneNameservers, setZoneNameservers] = useState([]);
    const [isBulkImportVisible, setIsBulkImportVisible] = useState(false);
    const searchInputRef = useRef(null);
    const [subscription, setSubscription] = useState(null);
    const [quotaWarning, setQuotaWarning] = useState(null);
    const [isDnssecVisible, setIsDnssecVisible] = useState(false);
    const [planDrawerOpen, setPlanDrawerOpen] = useState(() => new URLSearchParams(window.location.search).get('plan') === '1');

    // 调度线路配置 Modal 状态
    const [routeModalVisible, setRouteModalVisible] = useState(false);
    const [routeModalRecord, setRouteModalRecord] = useState(null);
    const [routeForm] = Form.useForm();

    // Analytics state
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [queryTrend, setQueryTrend] = useState([]);
    const [zoneStats, setZoneStats] = useState(null);
    const [dateRange, setDateRange] = useState('7d'); // 7d, 14d, 30d
    const [panelExpanded, setPanelExpanded] = useState(false); // 手风琴展开状态
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    const isEditing = (record) => record.id === editingKey;

    // 判断是否为受保护的系统记录（根NS和SOA不可编辑/删除）
    const isProtectedRecord = (record) => {
        const type = (record.type || '').toUpperCase();
        const name = record.name || '';
        if (type === 'SOA') return true;
        if (type === 'NS' && (name === '@' || name === '')) return true;
        return false;
    };

// 修改编辑记录函数
    const edit = (record) => {
        // 准备表单数据
        const formData = {
            ...record,
            // 为地理位置选择器准备数据
            geoData: {
                continent: record.continent || '0',
                country: record.country || '0',
                province: record.province || '0',
                city: record.city || '0'
            },
            // 为ISP选择器准备数据
            ispData: {
                isp_country: record.isp_country || '',
                isp_type: record.isp_type || '',
                isp: record.isp || '0'
            }
        };

        form.setFieldsValue(formData);
        setEditingKey(record.id);
    };


    const cancel = () => {
        if (newRecord) {
            setRecords(records.filter(record => record.id !== 'new-record'));
            setNewRecord(null);
        }
        setEditingKey('');
    };

    // 打开调度线路配置Modal
    const openRouteModal = (record) => {
        // 优先从主表单读取当前编辑的值，否则用record的原始值
        const currentGeoData = form.getFieldValue('geoData') || {};
        const currentIspData = form.getFieldValue('ispData') || {};
        const currentCidrId = form.getFieldValue('cidr_id');

        // 处理 cidr_id，确保格式一致（对象格式）
        let cidrValue = currentCidrId !== undefined ? currentCidrId : (record?.cidr_id || 0);
        if (typeof cidrValue === 'number') {
            cidrValue = { cidr_id: cidrValue, cidr_name: record?.cidr_name || '' };
        }

        const formValues = {
            geoData: {
                continent: currentGeoData.continent || record?.continent || '0',
                country: currentGeoData.country || record?.country || '0',
                province: currentGeoData.province || record?.province || '0',
                city: currentGeoData.city || record?.city || '0',
                continent_name: currentGeoData.continent_name || record?.continent_name || '',
                country_name: currentGeoData.country_name || record?.country_name || '',
                province_name: currentGeoData.province_name || record?.province_name || '',
                city_name: currentGeoData.city_name || record?.city_name || ''
            },
            ispData: {
                isp_country: currentIspData.isp_country || record?.isp_country || '',
                isp_type: currentIspData.isp_type || record?.isp_type || '',
                isp: currentIspData.isp || record?.isp || '0',
                isp_name: currentIspData.isp_name || record?.isp_name || ''
            },
            cidr_id: cidrValue
        };

        setRouteModalRecord(record);
        // 先重置表单再设置值
        routeForm.resetFields();
        routeForm.setFieldsValue(formValues);
        setRouteModalVisible(true);
    };

    // 保存调度线路配置
    const handleRouteModalOk = () => {
        const values = routeForm.getFieldsValue();

        // 处理 cidr_id (可能是对象或数字)
        const cidrValue = values.cidr_id;
        const cidrId = typeof cidrValue === 'object' ? (cidrValue?.cidr_id || 0) : (cidrValue || 0);
        const cidrName = typeof cidrValue === 'object' ? (cidrValue?.cidr_name || '') : '';

        // 将选择的值同步到主表单
        form.setFieldsValue({
            geoData: values.geoData,
            ispData: values.ispData,
            cidr_id: cidrValue // 保持原样传给表单，在save时再提取
        });

        // 同时更新当前编辑行的临时显示数据
        if (routeModalRecord) {
            const updatedRecords = records.map(r => {
                if (r.id === routeModalRecord.id) {
                    return {
                        ...r,
                        continent: values.geoData?.continent || '0',
                        country: values.geoData?.country || '0',
                        province: values.geoData?.province || '0',
                        city: values.geoData?.city || '0',
                        continent_name: values.geoData?.continent_name || '',
                        country_name: values.geoData?.country_name || '',
                        province_name: values.geoData?.province_name || '',
                        city_name: values.geoData?.city_name || '',
                        isp: values.ispData?.isp || '0',
                        isp_name: values.ispData?.isp_name || '',
                        isp_country: values.ispData?.isp_country || '',
                        isp_type: values.ispData?.isp_type || '',
                        cidr_id: cidrId,
                        cidr_name: cidrName
                    };
                }
                return r;
            });
            setRecords(updatedRecords);
        }

        setRouteModalVisible(false);
        setRouteModalRecord(null);
    };

    const addNewRecord = () => {
        if (editingKey !== '') return;

        // 检查是否达到记录数配额上限
        if (subscription && subscription.has_subscription) {
            const zoneQuotas = subscription.resource_quota_usage?.zones || {};
            const recordQuota = zoneQuotas[zoneId]?.quotas?.dns_records_per_zone_limit;
            if (recordQuota && !recordQuota.is_unlimited && (recordQuota.used ?? recordQuota.current ?? 0) >= (recordQuota.limit ?? 0)) {
                Modal.confirm({
                    title: t('Plan limit reached'),
                    icon: <WarningOutlined />,
                    content: (
                        <div>
                            <p>{t('This zone has reached the plan\'s record limit ({{value}} records).', { value: recordQuota.limit ?? recordQuota.limit_value })}</p>
                            <p>{t('Upgrade your plan for more quota.')}</p>
                        </div>
                    ),
                    okText: t('Upgrade plan'),
                    cancelText: t('Cancel'),
                    onOk: () => {
                        navigate(userPath('/billing/subscription'));
                    }
                });
                return;
            }
        }

        // 先重置表单
        form.resetFields();

        const newRecordData = {
            id: 'new-record',
            zone_id: zoneId,
            name: '',
            type: 'A',
            content: '',
            ttl: 3600,
            priority: 0,
            state: 'default',
            continent: '0',
            country: '0',
            province: '0',
            city: '0',
            geoData: {},
            isp: '0',
            cidr_id: 0,
            comment: ''
        };

        setRecords([newRecordData, ...records]);
        setNewRecord(newRecordData);
        form.setFieldsValue(newRecordData);
        setEditingKey('new-record');

        // 滚动到页面顶部以便编辑新记录
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // 域名还没接入 CDN:带上域名/主机名/源站去 CDN 接入向导(选套餐)
    const offerCdnOnboard = (name, content) => Modal.confirm({
        title: t('This domain is not on CDN yet'),
        okText: t('Add to CDN'),
        cancelText: t('Cancel'),
        onOk: () => navigate(userPath('/cdn/domains/new?' + new URLSearchParams({
            name: zoneName,
            hostname: !name || name === '@' ? zoneName : `${name}.${zoneName}`,
            origin: content || ''
        })))
    });

    // 修改保存记录函数
    const save = async (key) => {
        try {
            const row = await form.validateFields();

            // 从表单中提取地理位置数据和ISP数据
            const { geoData, ispData, cidr_id: cidrValue, ...baseData } = row;

            // 处理 cidr_id (可能是对象或数字)
            const cidrId = typeof cidrValue === 'object' ? (cidrValue?.cidr_id || 0) : (cidrValue || 0);

            const updatedData = {
                ...baseData,
                // 扩展地理位置数据
                continent: geoData?.continent || '0',
                country: geoData?.country || '0',
                province: geoData?.province || '0',
                city: geoData?.city || '0',
                // 扩展ISP数据
                isp_country: ispData?.isp_country || '',
                isp_type: ispData?.isp_type || '',
                isp: ispData?.isp || '0',
                // CIDR ID
                cidr_id: cidrId
            };

            setLoading(true);

            const isNewRecord = key === 'new-record';
            let result;

            if (isNewRecord) {
                // 创建新记录
                result = await dnsService.createDnsRecord({
                    ...updatedData,
                    zone_id: zoneId
                });
            } else {
                // 更新现有记录
                result = await dnsService.updateDnsRecord({
                    ...updatedData,
                    id: key
                });
            }

            if (result.success) {
                // 如果有 CDN 相关消息，显示详细提示
                const successMsg = result.message || (isNewRecord ? t('Record created') : t('Record updated'));
                message.success(successMsg);
                setEditingKey('');
                setNewRecord(null);
                fetchRecords(); // 刷新记录列表
            } else if (result.code === 'cdn_domain_missing') {
                const rec = records.find((r) => r.id === key) || {};
                if (!isNewRecord) fetchRecords(); // 其余改动已保存,只是没开成 CDN
                offerCdnOnboard(updatedData.name ?? rec.name, updatedData.content ?? rec.content);
            } else {
                message.error(result.error || (isNewRecord ? t('Failed to create record') : t('Failed to update record')));
            }
        } catch (errInfo) {
            console.log('Validate Failed:', errInfo);
        } finally {
            setLoading(false);
        }
    };


    const deleteRecord = async (id) => {
        try {
            setLoading(true);
            const result = await dnsService.deleteDnsRecord(id);

            if (result.success) {
                message.success(t('Record deleted'));
                fetchRecords(); // 刷新记录列表
            } else {
                message.error(result.error || t('Failed to delete record'));
            }
        } catch (error) {
            message.error(t('Failed to delete record: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };


    // 预加载和转换地理信息和CIDR名称
    const enhanceRecordsWithGeoInfo = async (records) => {
        if (!records || records.length === 0) return records;

        // 收集所有使用到的大洲、国家、省份、城市和ISP、CIDR的ID
        const continentIds = new Set();
        const countryIds = new Set();
        const provinceIds = new Set();
        const cityIds = new Set();
        const ispIds = new Set();
        const cidrIds = new Set();

        // 收集所有需要查询的ID
        records.forEach(record => {
            if (record.continent && record.continent !== '0') {
                continentIds.add(record.continent);
            }
            if (record.country && record.country !== '0') {
                countryIds.add(record.country);
            }
            if (record.province && record.province !== '0') {
                provinceIds.add(record.province);
            }
            if (record.city && record.city !== '0') {
                cityIds.add(record.city);
            }
            if (record.isp && record.isp !== '0') {
                ispIds.add(record.isp);
            }
            if (record.cidr_id && record.cidr_id > 0) {
                cidrIds.add(record.cidr_id);
            }
        });

        // 并行请求所有所需的地理信息
        const promises = [];

        // 获取大洲信息
        if (continentIds.size > 0) {
            promises.push(
                dnsService.getContinentInfo(Array.from(continentIds))
                    .catch(err => {
                        console.error('Error fetching continent info:', err);
                        return { success: false, data: {} };
                    })
            );
        } else {
            promises.push(Promise.resolve({ success: true, data: {} }));
        }

        // 获取国家信息
        if (countryIds.size > 0) {
            promises.push(
                dnsService.getCountryInfo(Array.from(countryIds))
                    .catch(err => {
                        console.error('Error fetching country info:', err);
                        return { success: false, data: {} };
                    })
            );
        } else {
            promises.push(Promise.resolve({ success: true, data: {} }));
        }

        // 获取省份信息
        if (provinceIds.size > 0) {
            promises.push(
                dnsService.getProvinceInfo(Array.from(provinceIds))
                    .catch(err => {
                        console.error('Error fetching province info:', err);
                        return { success: false, data: {} };
                    })
            );
        } else {
            promises.push(Promise.resolve({ success: true, data: {} }));
        }

        // 获取城市信息
        if (cityIds.size > 0) {
            promises.push(
                dnsService.getCityInfo(Array.from(cityIds))
                    .catch(err => {
                        console.error('Error fetching city info:', err);
                        return { success: false, data: {} };
                    })
            );
        } else {
            promises.push(Promise.resolve({ success: true, data: {} }));
        }

        // 获取ISP信息
        if (ispIds.size > 0) {
            promises.push(
                dnsService.getIspInfo(Array.from(ispIds))
                    .catch(err => {
                        console.error('Error fetching ISP info:', err);
                        return { success: false, data: {} };
                    })
            );
        } else {
            promises.push(Promise.resolve({ success: true, data: {} }));
        }

        // 获取CIDR信息
        if (cidrIds.size > 0) {
            promises.push(
                dnsService.getCidrInfo(Array.from(cidrIds))
                    .catch(err => {
                        console.error('Error fetching CIDR info:', err);
                        return { success: false, data: {} };
                    })
            );
        } else {
            promises.push(Promise.resolve({ success: true, data: {} }));
        }

        // 等待所有请求完成
        const [
            continentResponse,
            countryResponse,
            provinceResponse,
            cityResponse,
            ispResponse,
            cidrResponse
        ] = await Promise.all(promises);

        // 创建查找表
        const continentMap = continentResponse.success ? continentResponse.data : {};
        const countryMap = countryResponse.success ? countryResponse.data : {};
        const provinceMap = provinceResponse.success ? provinceResponse.data : {};
        const cityMap = cityResponse.success ? cityResponse.data : {};
        const ispMap = ispResponse.success ? ispResponse.data : {};
        const cidrMap = cidrResponse.success ? cidrResponse.data : {};

        // 增强记录数据
        return records.map(record => {
            const enhancedRecord = { ...record };

            // 添加地理名称信息
            if (record.continent && record.continent !== '0' && continentMap[record.continent]) {
                enhancedRecord.continent_name = continentMap[record.continent].name;
            }

            if (record.country && record.country !== '0' && countryMap[record.country]) {
                enhancedRecord.country_name = countryMap[record.country].country_code;
            }

            if (record.province && record.province !== '0' && provinceMap[record.province]) {
                enhancedRecord.province_name = provinceMap[record.province].name;
            }

            if (record.city && record.city !== '0' && cityMap[record.city]) {
                enhancedRecord.city_name = cityMap[record.city].name;
            }

            // 添加ISP名称
            if (record.isp && record.isp !== '0' && ispMap[record.isp]) {
                enhancedRecord.isp_name = ispMap[record.isp].name;
            }

            // 添加CIDR名称
            if (record.cidr_id && record.cidr_id > 0 && cidrMap[record.cidr_id]) {
                enhancedRecord.cidr_name = cidrMap[record.cidr_id].name;
            }

            return enhancedRecord;
        });
    };

    /*
// 在fetchDnsRecords函数中使用
    const fetchRecords = async (zoneId, page = 1, search = '', type = '', geo = '') => {
        setLoading(true);
        // 保存筛选状态到state
        // state.updateDnsFilters(page, search, type, geo);

        // 保存当前筛选器状态到URL
        if (page !== 1 || search || type || geo) {
            const queryParams = new URLSearchParams(window.location.search);
            if (page !== 1) queryParams.set('page', page);
            else queryParams.delete('page');

            if (search) queryParams.set('search', search);
            else queryParams.delete('search');

            if (type) queryParams.set('type', type);
            else queryParams.delete('type');

            if (geo) queryParams.set('geo', geo);
            else queryParams.delete('geo');

            const newUrl = window.location.pathname + (queryParams.toString() ? '?' + queryParams.toString() : '');
            window.history.replaceState({}, '', newUrl);
        } else {
            // 如果没有筛选器，清除URL参数
            window.history.replaceState({}, '', window.location.pathname);
        }

        let url = `/api/dns/records?zone_id=${zoneId}&page=${page}&limit=20`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (type) url += `&type=${encodeURIComponent(type)}`;
        if (geo) url += `&geo=${encodeURIComponent(geo)}`;

        try {
            const result = await dnsService.getDnsRecords({
                zone_id: zoneId,
                page: page,
                limit: 20,
                search: search,
                type: type,
                geo: geo
            });

            if (result.success) {
                // 添加记录计数和范围信息
                const pagination = result.data.pagination;
                const startRecord = (pagination.current_page - 1) * pagination.per_page + 1;
                const endRecord = Math.min(startRecord + pagination.per_page - 1, pagination.total);

                // 显示记录计数，在卡片的header部分
                const cardHeader = document.querySelector('.card-header .d-flex.justify-content-between');
                if (cardHeader) {
                    // 查找或创建计数显示元素
                    let countSpan = cardHeader.querySelector('.record-count');
                    if (!countSpan) {
                        countSpan = document.createElement('span');
                        countSpan.className = 'record-count';
                        cardHeader.insertBefore(countSpan, cardHeader.querySelector('.btn-group'));
                    }

                    // 设置计数文本
                    if (pagination.total > 0) {
                        countSpan.textContent = `显示 ${startRecord}-${endRecord} 共 ${pagination.total} 条记录`;
                    } else {
                        countSpan.textContent = '无记录';
                    }
                }

                // 增强记录数据

                // 更新表格数据
                setRecords(enhancedRecords);

                // 更新分页信息
                setPagination({
                    ...pagination,
                    current: pagination.current_page,
                    pageSize: pagination.per_page,
                    total: pagination.total
                });
            } else {
                message.error(result.error || '获取DNS记录失败');
            }
        } catch (error) {
            message.error('获取DNS记录失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };
    */


    const fetchRecords = async (params = {}) => {
        const { pageSize, current, ...restParams } = params;

        setLoading(true);
        try {
            const result = await dnsService.getDnsRecords({
                zone_id: zoneId,
                page: params.current || pagination.current,
                limit: params.pageSize || pagination.pageSize,
                search: filters.search,
                type: filters.type,
                geo: filters.geo,
                ...restParams
            });

            if (result.success) {
                const enhancedRecords = await enhanceRecordsWithGeoInfo(
                    Array.isArray(result.data?.records) ? result.data.records : []
                );

                setRecords(enhancedRecords);
                setPagination({
                    ...pagination,
                    current: result.data?.pagination?.current_page || result.data?.page || 1,
                    pageSize: result.data?.pagination?.per_page || params.pageSize || pagination.pageSize,
                    total: result.data?.pagination?.total || 0
                });
            } else {
                message.error(result.error || t('Failed to retrieve DNS records'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve DNS records: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const fetchSubscriptionInfo = async () => {
        try {
            // 传递zoneId以获取该zone的订阅信息
            const response = await billingService.getSubscriptionOverview('dns', zoneId);
            if (response.success && (response.data.has_subscription || response.data.is_free_version)) {
                setSubscription(response.data);

                // 检查记录数配额警告
                const zoneQuotas = response.data.resource_quota_usage?.zones || {};
                const zoneQuotaInfo = zoneQuotas[zoneId]?.quotas?.dns_records_per_zone_limit;
                const recordQuota = zoneQuotaInfo || response.data.quota_usage?.dns_records_per_zone_limit;

                if (recordQuota) {
                    const percentage = recordQuota.percentage ?? (recordQuota.limit > 0 ? Math.round((recordQuota.used ?? recordQuota.current ?? 0) / recordQuota.limit * 100) : 0);
                    const current = recordQuota.current ?? recordQuota.used ?? 0;
                    const limit = recordQuota.limit ?? recordQuota.limit_value ?? 0;

                    if (!recordQuota.is_unlimited && percentage >= 80) {
                        setQuotaWarning({
                            type: percentage >= 100 ? 'error' : 'warning',
                            message: percentage >= 100
                                ? t('This zone has reached the plan\'s record limit. New records cannot be created.')
                                : t('This zone is approaching its record limit ({{current}}/{{limit}})', { current, limit })
                        });
                    }
                }
            }
        } catch (error) {
            console.error('获取套餐信息失败：', error);
        }
    };

    // 加载解析统计数据
    const fetchAnalytics = async () => {
        setAnalyticsLoading(true);
        try {
            // 计算时间范围
            const days = dateRange === '7d' ? 7 : dateRange === '14d' ? 14 : 30;
            const endTime = Math.floor(Date.now() / 1000);
            const startTime = endTime - days * 86400;
            const interval = '1d'; // 按天聚合

            // 获取趋势数据
            const trendResult = await dnsService.getQueryTrend({
                zone: zoneName,
                start_time: startTime,
                end_time: endTime,
                interval: interval
            });

            if (trendResult.success && trendResult.data) {
                setQueryTrend(trendResult.data);
            }

            // 获取zone统计
            const statsResult = await dnsService.getZoneAnalytics(zoneId, {
                start_time: startTime,
                end_time: endTime
            });

            if (statsResult.success && statsResult.data) {
                setZoneStats(statsResult.data);
            }
        } catch (error) {
            console.error('获取解析统计失败：', error);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    // 初始化/更新图表
    const initChart = () => {
        if (!chartRef.current || !queryTrend || queryTrend.length === 0) return;

        if (chartInstance.current) {
            chartInstance.current.dispose();
        }

        chartInstance.current = echarts.init(chartRef.current);

        const dates = queryTrend.map(item => {
            const time = item.timestamp || item.time_bucket || item.time || item.date;
            // 格式化时间显示
            if (time) {
                // timestamp是秒，需要转换为毫秒
                const d = new Date(time * 1000);
                return `${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
            }
            return time;
        });
        const queries = queryTrend.map(item => item.count || item.queries || 0);

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line' }
            },
            grid: {
                left: 50,
                right: 20,
                bottom: 30,
                top: 20
            },
            xAxis: {
                type: 'category',
                data: dates,
                axisLabel: {
                    fontSize: 11,
                    rotate: dateRange === '7d' ? 45 : 0
                }
            },
            yAxis: {
                type: 'value',
                name: t('Query count'),
                axisLabel: { fontSize: 11 }
            },
            series: [{
                name: t('Query count'),
                type: 'line',
                data: queries,
                smooth: true,
                itemStyle: { color: '#1890ff' },
                areaStyle: { color: 'rgba(24, 144, 255, 0.15)' }
            }]
        };

        chartInstance.current.setOption(option);
    };

    useEffect(() => {
        fetchRecords();
        fetchSubscriptionInfo();
    }, [zoneId, filters]); // 当zoneId或filters变化时重新获取数据

    // 如果没有zoneName，从API获取
    useEffect(() => {
        const fetchZoneInfo = async () => {
            if (!zoneId) return;
            try {
                const result = await dnsService.getDnsZone(zoneId);
                if (result.success && result.data) {
                    setZoneNameservers(Array.isArray(result.data.nameservers) ? result.data.nameservers : []);
                    setZoneNsMigrating(!!result.data.ns_migrating);
                    if (!zoneName && result.data.zone) {
                        // 优先使用 display_zone（IDN域名显示Unicode），否则使用 zone
                        setZoneName(result.data.zone.display_zone || result.data.zone.zone);
                    }
                }
            } catch (error) {
                console.error('获取区域信息失败:', error);
            }
        };
        fetchZoneInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoneId]);

    // 加载解析统计
    useEffect(() => {
        if (zoneName) {
            fetchAnalytics();
        }
    }, [zoneId, dateRange, zoneName]);

    // 更新图表
    useEffect(() => {
        if (queryTrend && queryTrend.length > 0 && panelExpanded) {
            // 延迟初始化，确保DOM已渲染
            const timer = setTimeout(() => {
                initChart();
            }, 100);
            return () => clearTimeout(timer);
        }
        return () => {
            if (chartInstance.current) {
                chartInstance.current.dispose();
                chartInstance.current = null;
            }
        };
    }, [queryTrend, dateRange, panelExpanded]);

    // 窗口resize时更新图表
    useEffect(() => {
        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleTableChange = (pagination, filters, sorter) => {
        // 处理排序
        let sortField = null;
        let sortOrder = null;
        if (sorter && sorter.field && sorter.order) {
            sortField = sorter.field;
            sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
        }

        fetchRecords({
            current: pagination.current,
            pageSize: pagination.pageSize,
            sortField,
            sortOrder
        });
    };

    const handleSearch = () => {
        const searchValue = searchInputRef.current.input.value;
        setFilters({
            ...filters,
            search: searchValue
        });
        setPagination({
            ...pagination,
            current: 1 // 搜索时重置为第一页
        });
    };

    const handleFilterChange = (key, value) => {
        setFilters({
            ...filters,
            [key]: value
        });
        setPagination({
            ...pagination,
            current: 1 // 筛选时重置为第一页
        });
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            type: '',
            geo: ''
        });
        searchInputRef.current.input.value = '';
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleZoneDownload = async () => {
        try {
            const response = await apiClient.get(
                `/api/user/dns/zone-download?zone=${encodeURIComponent(zoneName)}`,
                { responseType: 'blob' }
            );
            const disposition = response.headers?.['content-disposition'] || '';
            const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/i);
            const filename = filenameMatch ? filenameMatch[1] : `${zoneName}.zone`;
            const url = URL.createObjectURL(new Blob([response.data], { type: 'text/plain' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(t('Failed to download Zone file: {{value}}', { value: e?.response?.data?.error || e.message }));
        }
    };

    const handleBulkImportOk = async (records) => {
        setLoading(true);
        try {
            const result = await dnsService.bulkImportRecords({
                zone_id: zoneId,
                records
            });

            if (result.success) {
                message.success(t('Imported {{success_count}} records', { success_count: result.data.success_count }));
                if (result.data.failed_count > 0) {
                    message.warning(t('{{failed_count}} records failed to import', { failed_count: result.data.failed_count }));
                }
                fetchRecords();
            } else {
                message.error(result.error || t('Bulk import failed'));
            }
        } catch (error) {
            message.error(t('Bulk import failed: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
            setIsBulkImportVisible(false);
        }
    };

    const getGeoTagColor = (info) => {
        if (info.startsWith('洲:')) return 'purple';
        if (info.startsWith('国家:')) return 'blue';
        if (info.startsWith('省份:')) return 'cyan';
        if (info.startsWith('城市:')) return 'green';
        if (info.startsWith('ISP:')) return 'orange';
        if (info.startsWith('CIDR:')) return 'red';
        return 'default';
    };


    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            width: 60,
            editable: true,
            sorter: true,
            ellipsis: true,
            render: (text) => {
                // 根记录 name 为 '@'，其 FQDN 即区域本身，不应拼成 "@.example.com"
                const fqdn = (text === '@' || !text) ? zoneName : `${text}.${zoneName}`;
                return <Tooltip title={fqdn}>{text}</Tooltip>;
            }
        },
        {
            title: t('Type'),
            dataIndex: 'type',
            key: 'type',
            width: 80,
            editable: true,
            sorter: true,
            render: (type) => <Tag color="blue">{type}</Tag>
        },
        {
            title: t('Content'),
            dataIndex: 'content',
            key: 'content',
            width: 150,
            editable: true,
            sorter: true,
            render: (text) => (
                <div style={{
                    wordBreak: 'break-all',
                    whiteSpace: 'pre-wrap',
                    maxHeight: 80,
                    overflow: 'auto',
                    lineHeight: '1.4',
                    fontSize: 13,
                }}>
                    {text}
                </div>
            ),
        },
        {
            title: 'TTL',
            dataIndex: 'ttl',
            key: 'ttl',
            width: 80,
            editable: true,
        },
        {
            title: t('Priority'),
            dataIndex: 'priority',
            key: 'priority',
            width: 70,
            editable: true,
            /*render: (priority, record) =>
                (record.type === 'MX' || record.type === 'SRV') ? priority : '-'*/
        },
        {
            title: t('Notes'),
            dataIndex: 'comment',
            key: 'comment',
            width: 100,
            editable: true,
            ellipsis: true,
        },
        {
            title: t('Traffic routing'),
            dataIndex: 'route',
            key: 'route',
            width: 180,
            editable: true,
            render: (_, record) => {
                if (record.type !== 'A' && record.type !== 'AAAA' && record.type !== 'CNAME') {
                    return <Text type="secondary">-</Text>;
                }

                // 构建显示文本
                const parts = [];

                // 地理位置部分
                if (record.city_name && record.city !== '0') {
                    parts.push(record.city_name);
                } else if (record.province_name && record.province !== '0') {
                    parts.push(record.province_name);
                } else if (record.country_name && record.country !== '0') {
                    parts.push(record.country_name);
                } else if (record.continent_name && record.continent !== '0') {
                    parts.push(record.continent_name);
                }

                // ISP部分
                if (record.isp_name && record.isp !== '0') {
                    parts.push(record.isp_name);
                }

                // 自定义线路部分
                if (record.cidr_name && record.cidr_id > 0) {
                    parts.push(record.cidr_name);
                }

                const isEditing = editingKey === record.id;
                const displayText = parts.length > 0 ? parts.join(' | ') : t('Global');

                // 构建完整的tooltip内容
                const tooltipLines = [];
                if (record.continent_name && record.continent !== '0') tooltipLines.push(t('Continent: {{continent_name}}', { continent_name: record.continent_name }));
                if (record.country_name && record.country !== '0') tooltipLines.push(t('Country: {{country_name}}', { country_name: record.country_name }));
                if (record.province_name && record.province !== '0') tooltipLines.push(t('Province: {{province_name}}', { province_name: record.province_name }));
                if (record.city_name && record.city !== '0') tooltipLines.push(t('City: {{city_name}}', { city_name: record.city_name }));
                if (record.isp_name && record.isp !== '0') tooltipLines.push(`ISP: ${record.isp_name}`);
                if (record.cidr_name && record.cidr_id > 0) tooltipLines.push(t('Route: {{cidr_name}}', { cidr_name: record.cidr_name }));

                const tooltipContent = tooltipLines.length > 0 ? (
                    <div>
                        {tooltipLines.map((line, idx) => (
                            <div key={idx}>{line}</div>
                        ))}
                    </div>
                ) : t('Global default');

                return (
                    <Tooltip title={tooltipContent} placement="topLeft">
                        <div
                            style={{
                                cursor: isEditing ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                            onClick={() => {
                                if (isEditing) {
                                    openRouteModal(record);
                                }
                            }}
                        >
                            <NodeIndexOutlined style={{ color: parts.length > 0 ? '#1890ff' : '#d9d9d9', fontSize: 14 }} />
                            {record.country && record.country !== '0' && <CountryFlag code={record.country_name} style={{ marginInlineEnd: 0 }} />}
                            <Text
                                style={{
                                    fontSize: 12,
                                    color: isEditing ? '#1890ff' : undefined,
                                    textDecoration: isEditing ? 'underline' : 'none',
                                    maxWidth: 140,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {displayText}
                            </Text>
                            {isEditing && <EditOutlined style={{ color: '#1890ff', fontSize: 12 }} />}
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            title: 'CDN',
            dataIndex: 'cdn_enabled',
            key: 'cdn_enabled',
            width: 50,
            align: 'center',
            render: (enabled, record) => {
                if (record.type !== 'A' && record.type !== 'AAAA' && record.type !== 'CNAME') {
                    return <Text type="secondary">-</Text>;
                }

                const handleCdnToggle = () => {
                    // 如果正在编辑此记录，直接切换表单值
                    if (editingKey === record.id) {
                        const currentValue = form.getFieldValue('cdn_enabled');
                        form.setFieldsValue({ cdn_enabled: !currentValue });
                        return;
                    }

                    // 非编辑模式，弹出确认框
                    Modal.confirm({
                        title: enabled ? t('Disable CDN acceleration') : t('Enable CDN acceleration'),
                        content: enabled
                            ? t('Turn off CDN acceleration for this record? It will also be turned off for other records with the same hostname.')
                            : t('Enable CDN acceleration for this record? It will also be enabled for other records with the same hostname.'),
                        okText: t('OK'),
                        cancelText: t('Cancel'),
                        onOk: async () => {
                            try {
                                const result = await dnsService.updateDnsRecord({
                                    id: record.id,
                                    cdn_enabled: !enabled
                                });
                                if (result.success) {
                                    message.success(result.message || (enabled ? t('CDN disabled') : t('CDN enabled')));
                                    fetchRecords();
                                } else if (result.code === 'cdn_domain_missing') {
                                    offerCdnOnboard(record.name, record.content);
                                } else {
                                    message.error(result.error || t('Operation failed'));
                                }
                            } catch (error) {
                                message.error(t('Operation failed: {{message}}', { message: error.message }));
                            }
                        }
                    });
                };

                const CloudIcon = enabled ? CloudFilled : CloudOutlined;
                return (
                    <Tooltip title={enabled ? t('CDN acceleration enabled (click to disable)') : t('CDN acceleration disabled (click to enable)')}>
                        <CloudIcon
                            style={{
                                fontSize: 18,
                                color: enabled ? '#1890ff' : '#d9d9d9',
                                cursor: 'pointer'
                            }}
                            onClick={handleCdnToggle}
                        />
                    </Tooltip>
                );
            }
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 150,
            fixed: isMobile ? '' : 'right',
            render: (_, record) => {
                const editable = isEditing(record);
                return editable ? (
                    <Space>
                        <Button
                            type="link"
                            icon={<SaveOutlined />}
                            onClick={() => save(record.id)}
                            style={{ paddingLeft: 0, paddingRight: 0 }}
                        >
                            {t('Save')}
                        </Button>
                        <Button
                            type="link"
                            icon={<CloseOutlined />}
                            onClick={cancel}
                            style={{ paddingLeft: 0, paddingRight: 0 }}
                            danger
                        >
                            {t('Cancel')}
                        </Button>
                    </Space>
                ) : (
                    <Space>
                        <Tooltip title={isProtectedRecord(record) ? t('System record, cannot be modified') : ''}>
                            <Button
                                type="link"
                                disabled={editingKey !== '' || isProtectedRecord(record)}
                                onClick={() => edit(record)}
                                icon={<EditOutlined />}
                                style={{ paddingLeft: 0, paddingRight: 0 }}
                            >
                                {t('Edit')}
                            </Button>
                        </Tooltip>
                        {isProtectedRecord(record) ? (
                            <Tooltip title={t('System record, cannot be deleted')}>
                                <Button
                                    type="link"
                                    disabled
                                    danger
                                    icon={<DeleteOutlined />}
                                    style={{ paddingLeft: 0, paddingRight: 0 }}
                                >
                                    {t('Delete')}
                                </Button>
                            </Tooltip>
                        ) : (
                            <Popconfirm
                                title={t('Delete this record?')}
                                onConfirm={() => deleteRecord(record.id)}
                                okText={t('OK')}
                                cancelText={t('Cancel')}
                            >
                                <Button
                                    type="link"
                                    disabled={editingKey !== ''}
                                    danger
                                    icon={<DeleteOutlined />}
                                    style={{ paddingLeft: 0, paddingRight: 0 }}
                                >
                                    {t('Delete')}
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                );
            },
        },
    ];

    // 移动端记录卡片组件
    const RecordCard = ({ record }) => {
        const { t } = useTranslation();
        const editable = isEditing(record);
        const isGeoEnabled = ['A', 'AAAA', 'CNAME'].includes(record.type);

        // 构建地理位置显示
        const getGeoDisplay = () => {
            if (!isGeoEnabled) return null;
            const geoInfo = [];
            if (record.continent_name && record.continent !== '0') geoInfo.push(record.continent_name);
            if (record.country_name && record.country !== '0') geoInfo.push(record.country_name);
            if (record.province_name && record.province !== '0') geoInfo.push(record.province_name);
            if (record.city_name && record.city !== '0') geoInfo.push(record.city_name);
            return geoInfo.length > 0 ? geoInfo.join(' > ') : '全局';
        };

        if (editable) {
            // 编辑模式下的卡片
            return (
                <Card
                    size="small"
                    style={{ marginBottom: 12 }}
                    className="dns-record-card editing"
                >
                    <Form form={form} component={false}>
                        <Row gutter={[8, 8]}>
                            <Col span={12}>
                                <Form.Item name="name" label={t('Name')} style={{ marginBottom: 8 }} rules={[{ required: true }]}>
                                    <Input placeholder={t('Record name')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="type" label={t('Type')} style={{ marginBottom: 8 }} rules={[{ required: true }]}>
                                    <Select>
                                        <Option value="A">A</Option>
                                        <Option value="AAAA">AAAA</Option>
                                        <Option value="CNAME">CNAME</Option>
                                        <Option value="MX">MX</Option>
                                        <Option value="TXT">TXT</Option>
                                        <Option value="NS">NS</Option>
                                        <Option value="SRV">SRV</Option>
                                        <Option value="CAA">CAA</Option>
                                        <Option value="PTR">PTR</Option>
                                        <Option value="HTTPS">HTTPS</Option>
                                        <Option value="SVCB">SVCB</Option>
                                        <Option value="NAPTR">NAPTR</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="content" label={t('Content')} style={{ marginBottom: 8 }} dependencies={['type']}
                                    rules={[recordContentRule(() => form.getFieldValue('type') || record.type)]}>
                                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder={t(CONTENT_PLACEHOLDERS[form.getFieldValue('type') || record.type] || '记录内容')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="ttl" label="TTL" style={{ marginBottom: 8 }}>
                                    <Input type="number" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="priority" label={t('Priority')} style={{ marginBottom: 8 }}>
                                    <Input type="number" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="comment" label={t('Notes')} style={{ marginBottom: 8 }}>
                                    <Input placeholder={t('Notes')} />
                                </Form.Item>
                            </Col>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
                            >
                                {({ getFieldValue }) => {
                                    const currentType = getFieldValue('type') || record.type;
                                    const showGeo = ['A', 'AAAA', 'CNAME'].includes(currentType);
                                    if (!showGeo) return null;

                                    // 构建当前选择的调度线路显示
                                    const geoData = getFieldValue('geoData') || {};
                                    const ispData = getFieldValue('ispData') || {};
                                    const cidrId = getFieldValue('cidr_id') || 0;
                                    const routeParts = [];
                                    if (geoData.city_name) routeParts.push(geoData.city_name);
                                    else if (geoData.province_name) routeParts.push(geoData.province_name);
                                    else if (geoData.country_name) routeParts.push(geoData.country_name);
                                    if (ispData.isp_name) routeParts.push(ispData.isp_name);
                                    if (record.cidr_name && cidrId > 0) routeParts.push(record.cidr_name);
                                    const routeText = routeParts.length > 0 ? routeParts.join(' | ') : t('Global');

                                    return (
                                        <>
                                            <Col span={24}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Traffic routing')}</Text>
                                                    <div
                                                        onClick={() => openRouteModal(record)}
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: '1px solid #d9d9d9',
                                                            borderRadius: 6,
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            marginTop: 4
                                                        }}
                                                    >
                                                        <Space>
                                                            <NodeIndexOutlined style={{ color: routeParts.length > 0 ? '#1890ff' : '#d9d9d9' }} />
                                                            <Text>{routeText}</Text>
                                                        </Space>
                                                        <EditOutlined style={{ color: '#1890ff' }} />
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col span={24}>
                                                <Form.Item name="cdn_enabled" label={t('CDN acceleration')} valuePropName="checked" style={{ marginBottom: 8 }}>
                                                    <Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} />
                                                </Form.Item>
                                            </Col>
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Row>
                        <Divider style={{ margin: '12px 0' }} />
                        <Space>
                            <Button type="primary" icon={<SaveOutlined />} onClick={() => save(record.id)} loading={loading}>
                                {t('Save')}
                            </Button>
                            <Button icon={<CloseOutlined />} onClick={cancel}>
                                {t('Cancel')}
                            </Button>
                        </Space>
                    </Form>
                </Card>
            );
        }

        // 非编辑模式下的卡片
        return (
            <Card
                size="small"
                style={{ marginBottom: 12 }}
                className="dns-record-card"
                actions={[
                    <Tooltip title={isProtectedRecord(record) ? t('System record, cannot be modified') : ''} key="edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            disabled={editingKey !== '' || isProtectedRecord(record)}
                            onClick={() => edit(record)}
                        >
                            {t('Edit')}
                        </Button>
                    </Tooltip>,
                    isProtectedRecord(record) ? (
                        <Tooltip title={t('System record, cannot be deleted')} key="delete">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                disabled
                            >
                                {t('Delete')}
                            </Button>
                        </Tooltip>
                    ) : (
                        <Popconfirm
                            title={t('Delete this record?')}
                            onConfirm={() => deleteRecord(record.id)}
                            okText={t('OK')}
                            cancelText={t('Cancel')}
                            key="delete"
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={editingKey !== ''}
                            >
                                {t('Delete')}
                            </Button>
                        </Popconfirm>
                    )
                ]}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                        <Text strong style={{ fontSize: 15 }}>
                            {record.name || '@'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>.{zoneName}</Text>
                    </div>
                    <Tag color="blue">{record.type}</Tag>
                </div>

                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Content:')}</Text>
                    <Text style={{ wordBreak: 'break-all' }}>{record.content}</Text>
                </div>

                <Row gutter={[8, 4]} style={{ fontSize: 12 }}>
                    <Col span={12}>
                        <Text type="secondary">TTL: </Text>
                        <Text>{record.ttl}</Text>
                    </Col>
                    {(record.type === 'MX' || record.type === 'SRV') && (
                        <Col span={12}>
                            <Text type="secondary">{t('Priority: ')}</Text>
                            <Text>{record.priority}</Text>
                        </Col>
                    )}
                </Row>

                {isGeoEnabled && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Space size={4}>
                            <NodeIndexOutlined style={{ color: getGeoDisplay() !== '全局' ? '#1890ff' : '#d9d9d9', fontSize: 14 }} />
                            {record.country && record.country !== '0' && <CountryFlag code={record.country_name} style={{ marginInlineEnd: 0 }} />}
                            <Text style={{ fontSize: 12, color: '#666' }}>
                                {(() => {
                                    const parts = [];
                                    if (record.city_name && record.city !== '0') parts.push(record.city_name);
                                    else if (record.province_name && record.province !== '0') parts.push(record.province_name);
                                    else if (record.country_name && record.country !== '0') parts.push(record.country_name);
                                    else if (record.continent_name && record.continent !== '0') parts.push(record.continent_name);
                                    if (record.isp_name && record.isp !== '0') parts.push(record.isp_name);
                                    if (record.cidr_name && record.cidr_id > 0) parts.push(record.cidr_name);
                                    return parts.length > 0 ? parts.join(' | ') : t('Global');
                                })()}
                            </Text>
                        </Space>
                        <Tooltip title={record.cdn_enabled ? t('CDN acceleration enabled') : t('CDN acceleration disabled')}>
                            {record.cdn_enabled
                                ? <CloudFilled style={{ fontSize: 18, color: '#1890ff' }} />
                                : <CloudOutlined style={{ fontSize: 18, color: '#d9d9d9' }} />
                            }
                        </Tooltip>
                    </div>
                )}

                {record.comment && (
                    <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Notes: {{comment}}', { comment: record.comment })}</Text>
                    </div>
                )}
            </Card>
        );
    };

    const mergedColumns = columns.map(col => {
        if (!col.editable) {
            return col;
        }

        let baseInputType = 'text';
        let options = [];

        if (col.dataIndex === 'type') {
            baseInputType = 'select';
            options = [
                { value: 'A', label: t('A (IPv4 address)') },
                { value: 'AAAA', label: t('AAAA (IPv6 address)') },
                { value: 'CNAME', label: t('CNAME (alias)') },
                { value: 'MX', label: t('MX (mail server)') },
                { value: 'TXT', label: t('TXT (text record)') },
                { value: 'NS', label: t('NS (name server)') },
                { value: 'SRV', label: t('SRV (service record)') },
                { value: 'CAA', label: t('CAA (certificate authority authorization)') },
                { value: 'PTR', label: t('PTR (reverse DNS)') },
                { value: 'HTTPS', label: t('HTTPS (service binding)') },
                { value: 'SVCB', label: t('SVCB (service binding)') },
                { value: 'NAPTR', label: t('NAPTR (Naming Authority Pointer)') },
            ];
        } else if (col.dataIndex === 'geo') {
            baseInputType = 'geo';
        }

        return {
            ...col,
            onCell: (record) => {
                // TXT 和 SPF 类型的 content 字段使用 textarea
                let inputType = baseInputType;
                if (col.dataIndex === 'content' && (record?.type === 'TXT' || record?.type === 'SPF')) {
                    inputType = 'textarea';
                }
                return {
                    record,
                    inputType,
                    dataIndex: col.dataIndex,
                    title: col.title,
                    options,
                    editing: isEditing(record),
                };
            },
        };
    });
    let a = useStyle();

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Row gutter={[8, 8]} align="middle" justify="space-between">
                    <Col xs={24} sm={12}>
                        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>
                            {isMobile ? (zoneName || t('Loading...')) : t('Zone records: {{value}}', { value: zoneName || t('Loading...') })}
                        </Title>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Space wrap style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                            {!isMobile && (
                                <Button onClick={() => navigate(userPath('/dns/zones'))}>
                                    {t('Back to zones')}
                                </Button>
                            )}
                            <Button type="primary" icon={<PlusOutlined />} onClick={addNewRecord} disabled={editingKey !== ''}>
                                {isMobile ? t('Add') : t('Add record')}
                            </Button>
                            <Dropdown
                                overlay={
                                    <Menu>
                                        {isMobile && (
                                            <Menu.Item key="0" onClick={() => navigate(userPath('/dns/zones'))}>
                                                {t('Back to zones')}
                                            </Menu.Item>
                                        )}
                                        {isMobile && <Menu.Divider />}
                                        <Menu.Item key="1" icon={<FileExcelOutlined />} onClick={() => setIsBulkImportVisible(true)}>
                                            {t('Bulk import')}
                                        </Menu.Item>
                                        <Menu.Item key="4" icon={<FileTextOutlined />} onClick={handleZoneDownload} disabled={!zoneName}>
                                            {t('Download Zone file')}
                                        </Menu.Item>
                                        <Menu.Divider />
                                        <Menu.Item key="3" icon={<SafetyCertificateOutlined />} onClick={() => setIsDnssecVisible(true)}>
                                            {t('DNSSEC settings')}
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button icon={<SettingOutlined />}>
                                    {isMobile ? '' : t('More actions')} <DownOutlined />
                                </Button>
                            </Dropdown>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* 套餐配额提示 */}
            {quotaWarning && (
                <Alert
                    message={quotaWarning.message}
                    type={quotaWarning.type}
                    showIcon
                    closable
                    style={{ marginBottom: 16 }}
                    action={
                        <Button size="small" type="primary" onClick={() => navigate(userPath('/billing/subscription'))}>
                            {t('Upgrade plan')}
                        </Button>
                    }
                />
            )}

            {/* 套餐和解析统计 - 手风琴 */}
            {subscription && (subscription.has_subscription || subscription.is_free_version) && (
                <Collapse
                    style={{ marginBottom: 16 }}
                    expandIconPosition="end"
                    onChange={(keys) => setPanelExpanded(keys.length > 0)}
                    items={[{
                        key: '1',
                        label: (
                            <Space size="large" wrap>
                                <span>
                                    <Text type="secondary">{t('Current plan:')}</Text>
                                    <Text strong style={{ marginLeft: 4 }}>{subscription.product_name}</Text>
                                </span>
                                {(() => {
                                    const zoneQuotas = subscription.resource_quota_usage?.zones || {};
                                    const recordQuota = zoneQuotas[zoneId]?.quotas?.dns_records_per_zone_limit
                                        || subscription.quota_usage?.dns_records_per_zone_limit;
                                    if (!recordQuota) return null;
                                    const current = recordQuota.used ?? recordQuota.current ?? 0;
                                    const limit = recordQuota.limit ?? recordQuota.limit_value ?? 0;
                                    return (
                                        <span>
                                            <Text type="secondary">{t('Records:')}</Text>
                                            {recordQuota.is_unlimited ? (
                                                <Tag color="green" style={{ marginLeft: 4 }}>{t('Unlimited')}</Tag>
                                            ) : (
                                                <Text style={{ marginLeft: 4 }}>{current} / {limit}</Text>
                                            )}
                                        </span>
                                    );
                                })()}
                                {zoneStats && (
                                    <span>
                                        <Text type="secondary">{t('Recent queries:')}</Text>
                                        <Text strong style={{ marginLeft: 4 }}>
                                            {(zoneStats.total_queries || 0).toLocaleString()}
                                        </Text>
                                    </span>
                                )}
                                <LineChartOutlined style={{ color: '#1890ff' }} />
                            </Space>
                        ),
                        children: (
                            <div>
                                {/* 时间范围选择 */}
                                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                                    <Col>
                                        <Space>
                                            <Text type="secondary">{t('DNS query trends')}</Text>
                                            <Select
                                                value={dateRange}
                                                onChange={setDateRange}
                                                style={{ width: 100 }}
                                                size="small"
                                            >
                                                <Option value="7d">{t('Last 7 days')}</Option>
                                                <Option value="14d">{t('Last 14 days')}</Option>
                                                <Option value="30d">{t('Last 30 days')}</Option>
                                            </Select>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <Button
                                                size="small"
                                                type="primary"
                                                onClick={() => setPlanDrawerOpen(true)}
                                            >
                                                {t('Plan')}
                                            </Button>
                                            <Button
                                                size="small"
                                                icon={<BarChartOutlined />}
                                                onClick={() => navigate(userPath(`/dns/analytics?zone_id=${zoneId}`))}
                                            >
                                                {t('Detailed statistics')}
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>

                                {/* 解析图表 */}
                                <div
                                    ref={chartRef}
                                    style={{
                                        width: '100%',
                                        height: 200,
                                        marginBottom: 16
                                    }}
                                />
                                {analyticsLoading && (
                                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                        <Text type="secondary">{t('Loading...')}</Text>
                                    </div>
                                )}
                                {!analyticsLoading && queryTrend.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                        <Text type="secondary">{t('No DNS data yet')}</Text>
                                    </div>
                                )}

                                {/* 统计和套餐限制 */}
                                <Row gutter={[16, 16]}>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title={t('Total queries')}
                                            value={zoneStats?.total_queries || 0}
                                            valueStyle={{ fontSize: 18 }}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title={t('Unique query names')}
                                            value={zoneStats?.unique_qnames || 0}
                                            valueStyle={{ fontSize: 18 }}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('QPS limit')}</Text>
                                            <div style={{ fontSize: 18 }}>
                                                {subscription.resource_features?.dns_qps_limit ?
                                                    t('{{dns_qps_limit}}/second', { dns_qps_limit: subscription.resource_features.dns_qps_limit }) :
                                                    <Tag color="green">{t('Unlimited')}</Tag>
                                                }
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Resolution granularity')}</Text>
                                            <div style={{ fontSize: 18 }}>
                                                {subscription.resource_features?.dns_resolution_precision === 'country' ? t('Country level') :
                                                 subscription.resource_features?.dns_resolution_precision === 'province' ? t('Provincial level') :
                                                 subscription.resource_features?.dns_resolution_precision === 'city' ? t('City level') :
                                                 subscription.resource_features?.dns_resolution_precision || '-'
                                                }
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <Divider style={{ margin: '16px 0' }} />
                                <Row gutter={16}>
                                    <Col span={6}>
                                        <Text type="secondary">{t('DNSSEC:')}</Text>
                                        {subscription.resource_features?.dns_dnssec ?
                                            <Tag color="green">{t('Support')}</Tag> : <Tag>{t('Not supported')}</Tag>
                                        }
                                    </Col>
                                    <Col span={6}>
                                        <Text type="secondary">{t('Anycast:')}</Text>
                                        {subscription.resource_features?.dns_anycast ?
                                            <Tag color="green">{t('Support')}</Tag> : <Tag>{t('Not supported')}</Tag>
                                        }
                                    </Col>
                                    <Col span={6}>
                                        <Text type="secondary">{t('CIDR lists per zone:')}</Text>
                                        {subscription.resource_features?.dns_cidr_per_zone_limit === -1 ?
                                            <Tag color="green">{t('Unlimited')}</Tag> :
                                            <Text>{subscription.resource_features?.dns_cidr_per_zone_limit || '-'}</Text>
                                        }
                                    </Col>
                                    <Col span={6}>
                                        <Text type="secondary">{t('Expires at:')}</Text>
                                        <Text>{subscription.end_date ? dayjs(subscription.end_date).format('YYYY-MM-DD') : '-'}</Text>
                                    </Col>
                                </Row>
                            </div>
                        )
                    }]}
                />
            )}

            {zoneNameservers.length > 0 && (
                <Card
                    size="small"
                    title={<span><NodeIndexOutlined /> {t('Nameservers (NS)')}{zoneNsMigrating && (
                        <Tooltip title={t('Plan changed. Update the NS at your registrar to the following (the old NS still resolve DNS).')}><Tag color="orange" style={{ marginInlineStart: 8 }}>{t('NS update pending')}</Tag></Tooltip>
                    )}</span>}
                    extra={<Text type="secondary">{t('Please change the NS at your domain registrar to the following addresses')}</Text>}
                    style={{ marginBottom: 16 }}
                    bordered={false}
                >
                    <Space wrap>
                        {zoneNameservers.map((ns) => (
                            <Tag color="blue" key={ns}>
                                <Text copyable={{ text: ns }} style={{ color: 'inherit' }}>{ns}</Text>
                            </Tag>
                        ))}
                    </Space>
                </Card>
            )}

            <Card className="card-with-search-wrapper" bordered={false}>
                <div style={{ marginBottom: 16 }}>
                    <Row gutter={[8, 8]}>
                        <Col xs={24} sm={8}>
                            <Input
                                placeholder={isMobile ? t('Search...') : t('Search record name, content, or comment')}
                                prefix={<SearchOutlined />}
                                ref={searchInputRef}
                                onKeyPress={handleKeyPress}
                                style={{ width: '100%' }}
                                allowClear
                            />
                        </Col>
                        <Col xs={12} sm={4}>
                            <Select
                                placeholder={t('Type')}
                                style={{ width: '100%' }}
                                onChange={(value) => handleFilterChange('type', value)}
                                value={filters.type || undefined}
                                allowClear
                            >
                                <Option value="">{t('All')}</Option>
                                <Option value="A">A</Option>
                                <Option value="AAAA">AAAA</Option>
                                <Option value="CNAME">CNAME</Option>
                                <Option value="MX">MX</Option>
                                <Option value="TXT">TXT</Option>
                                <Option value="NS">NS</Option>
                                <Option value="SRV">SRV</Option>
                                <Option value="CAA">CAA</Option>
                                <Option value="PTR">PTR</Option>
                                <Option value="HTTPS">HTTPS</Option>
                                <Option value="SVCB">SVCB</Option>
                                <Option value="NAPTR">NAPTR</Option>
                            </Select>
                        </Col>
                        <Col xs={12} sm={4}>
                            <Select
                                placeholder={t('Location')}
                                style={{ width: '100%' }}
                                onChange={(value) => handleFilterChange('geo', value)}
                                value={filters.geo || undefined}
                                allowClear
                            >
                                <Option value="">{t('All')}</Option>
                                <Option value="geo">{t('Geolocation')}</Option>
                                <Option value="default">{t('Global default')}</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Space wrap>
                                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                                    {isMobile ? '' : t('Search')}
                                </Button>
                                <Button icon={<FilterOutlined />} onClick={clearFilters}>
                                    {isMobile ? '' : t('Clear')}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>

                {isMobile ? (
                    /* 移动端卡片视图 */
                    <div>
                        <List
                            dataSource={records}
                            loading={loading}
                            renderItem={(record) => (
                                <RecordCard key={record.id} record={record} />
                            )}
                            locale={{ emptyText: t('No records yet') }}
                        />
                        {/* 移动端分页 */}
                        <div style={{ textAlign: 'center', marginTop: 16 }}>
                            <Space direction="vertical" size={8}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {pagination.total > 0 ? t('{{total}} records total', { total: pagination.total }) : ''}
                                </Text>
                                <Space>
                                    <Button
                                        size="small"
                                        disabled={pagination.current <= 1}
                                        onClick={() => handleTableChange({ current: pagination.current - 1, pageSize: pagination.pageSize }, {}, {})}
                                    >
                                        {t('Previous page')}
                                    </Button>
                                    <Text>{pagination.current} / {Math.ceil(pagination.total / pagination.pageSize) || 1}</Text>
                                    <Button
                                        size="small"
                                        disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                                        onClick={() => handleTableChange({ current: pagination.current + 1, pageSize: pagination.pageSize }, {}, {})}
                                    >
                                        {t('Next page')}
                                    </Button>
                                </Space>
                            </Space>
                        </div>
                    </div>
                ) : (
                    /* 桌面端表格视图 */
                    <Form form={form} component={false}>
                        <Table
                            className={styles.customTable}
                            components={{
                                body: {
                                    cell: EditableCell,
                                },
                            }}

                            dataSource={records}
                            columns={mergedColumns}
                            rowKey="id"
                            pagination={pagination}
                            loading={loading}
                            onChange={handleTableChange}
                            size="middle"
                            rowClassName={() => 'editable-row'}
                            columnWidth={{ minimum: 40 }}
                            showSorterTooltip={false}
                            sortDirections={['ascend', 'descend']}
                            scroll={{ x: 'calc(700px + 50%)', y: 600 }}
                            locale={{
                                emptyText: <Empty description={t('No DNS records yet. Click "Add record" above.')} />
                            }}
                        />
                    </Form>
                )}
            </Card>

            <RecordBulkImportModal
                visible={isBulkImportVisible}
                onCancel={() => setIsBulkImportVisible(false)}
                onOk={handleBulkImportOk}
                zoneName={zoneName}
            />

            {/* 调度线路配置 Modal */}
            <Modal
                title={t('Traffic routing configuration')}
                open={routeModalVisible}
                onOk={handleRouteModalOk}
                onCancel={() => {
                    setRouteModalVisible(false);
                    setRouteModalRecord(null);
                }}
                width={500}
            >
                <Form form={routeForm} layout="vertical">
                    <Form.Item name="geoData" label={t('Geolocation')}>
                        <GeoSelector />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.geoData?.country_code !== currentValues.geoData?.country_code
                        }
                    >
                        {({ getFieldValue }) => {
                            const geoData = getFieldValue('geoData') || {};
                            return (
                                <Form.Item name="ispData" label={t('ISP')}>
                                    <IspSelector geoCountry={geoData.country_code || ''} />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    <Form.Item name="cidr_id" label={t('Custom route')}>
                        <CidrSelector />
                    </Form.Item>
                </Form>
            </Modal>

            <Drawer title={t('Plan')} width={560} open={planDrawerOpen} onClose={() => setPlanDrawerOpen(false)} destroyOnClose>
                <ResourcePlanCard type="dns" resourceId={Number(zoneId)} onChanged={fetchSubscriptionInfo} />
            </Drawer>

            <DnsDnssec
                visible={isDnssecVisible}
                onClose={() => setIsDnssecVisible(false)}
                zoneId={zoneId}
                zoneName={zoneName}
            />
        </div>
    );
};

export default DnsRecords;
